import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { trpc } from '@/config/trpc'
import { useUserContext } from '@/context/userLogin'
import { useEffect } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { usePostHog } from 'posthog-js/react'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/shadcn/form'

type Props = {
  email: string
  onEmailChange: (email: string) => void
  onSwitchToLogin: () => void
  onSwitchToRegisterSuccess: () => void
}

const formSchema = z
  .object({
    email: z
      .string()
      .min(1, { message: 'Email is required' })
      .email({ message: 'Please enter a valid email address' }),
    password: z.string().min(1, { message: 'Password is required' }),
    confirmPassword: z.string().min(1, { message: 'Password confirmation is required' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export function RegisterForm({
  email,
  onEmailChange,
  onSwitchToLogin,
  onSwitchToRegisterSuccess,
}: Props) {
  const { onAuthSuccess } = useUserContext()
  const posthog = usePostHog()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || '',
      password: '',
      confirmPassword: '',
    },
  })

  const { mutateAsync: register, isPending } = trpc.auth.register.useMutation({
    onError: (error) => {
      form.setError('root', { message: error.message })
      posthog.capture('auth_register_failed', {
        reason:
          error.message === 'Email already registered' ? 'email_already_registered' : 'unknown',
      })
    },
  })

  const watchedEmail = form.watch('email') // eslint-disable-line react-hooks/incompatible-library -- React Hook Form API

  useEffect(() => {
    if (watchedEmail !== email) {
      onEmailChange(watchedEmail)
    }
  }, [watchedEmail, email, onEmailChange])

  const handleRegister = async (values: z.infer<typeof formSchema>) => {
    posthog.capture('auth_register_attempted')
    try {
      const res = await register({ email: values.email, password: values.password })
      posthog.capture('auth_register_succeeded', { needs_verification: res.needsVerification })
      if (res.needsVerification) {
        // The verification link opens a fresh page load, and without cookie
        // consent PostHog keeps its anonymous id in memory only, so everything
        // this tab captured (landing, AppSumo claim, signup) would be orphaned
        // from the person who logs in afterwards. Identifying now, with the same
        // email-as-distinct-id login uses, stitches it without storing anything.
        // The email is unverified: at worst someone's pre-signup events land
        // on the profile of the address they typed.
        posthog.identify(values.email)
        onSwitchToRegisterSuccess()
      } else {
        onAuthSuccess(res.sessionToken, res.refreshToken)
      }
    } catch {
      // onError mutation callback already set the form-level error and captured the failed event.
    }
  }

  return (
    <div className="">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleRegister)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Email" size="lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input placeholder="Password" type="password" size="lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Repeat Password</FormLabel>
                <FormControl>
                  <Input placeholder="Repeat Password" type="password" size="lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.formState.errors.root && (
            <div className="mt-2">
              <FormMessage>{form.formState.errors.root.message}</FormMessage>
            </div>
          )}

          <Button
            type="submit"
            disabled={isPending}
            className="mt-4 w-full"
            size="lg"
            variant="primary">
            {isPending ? (
              <span className="border-primary/30 border-t-primary inline-block h-4 w-4 animate-spin rounded-full border-2" />
            ) : (
              'Create account'
            )}
          </Button>
        </form>
      </Form>

      <p className="text-muted-foreground mt-2 text-center text-xs">
        Already have an account?{' '}
        <a
          className="text-primary cursor-pointer underline"
          onClick={(e) => {
            e.preventDefault()
            onSwitchToLogin()
          }}>
          Login
        </a>
      </p>
    </div>
  )
}
